import Queue from 'queue';
import { print } from 'graphql';
import { enableMapSet, immerable, produce } from 'immer';
import { camelize, classify, pluralize } from 'inflected';
import { isNil, uniq } from 'lodash';
import ApplicationModel, {
  type ApplicationModelConstructor,
} from './ApplicationModel';
import { NotFoundError } from './Errors';
import Fragment from './Fragment';
import { memoize } from './lib/memoize';
import { modelObject } from './modelBuilder';
import type ModelCollection from './ModelCollection';
import { type QueryOptions } from './ApolloModelClient';

enableMapSet();

function mergeArguments(args, newArgs) {
  args = { ...args };

  Object.keys(newArgs).forEach(key => {
    if (Array.isArray(args[key])) {
      args[key] = args[key].concat(newArgs[key]);
    } else if (args[key]) {
      args[key] = [].concat(args[key], newArgs[key]);
    } else {
      args[key] = newArgs[key];
    }

    if (Array.isArray(args[key])) {
      args[key] = uniq(args[key]);
    }
  });

  return args;
}

const PAGINATION_ARGUMENTS = ['per', 'page'];
function canPaginate(args) {
  return PAGINATION_ARGUMENTS.some(
    paginationArg => !isNil(args?.[paginationArg])
  );
}

type Subquery = Query<any, any> | string[] | { [index: string]: Subquery };

export type Subqueries = Record<string, Subquery>;

type SelectionObject = Record<string, Record<string, any>>;
export type Selection = string | SelectionObject;
export type Selections = Selection[] | Set<Selection>;
export type QueryModel<Q extends Query<any, any>> = Q extends Query<
  any,
  infer M
>
  ? M
  : never;

type SortOrder = 'ASC' | 'DESC';

interface FindInBatchesOptions {
  concurrency?: number;
}
type FindInBatchesCallback<I extends ApplicationModel> = (
  page: ModelCollection<I>
) => void | Promise<void>;

interface FindOptions {
  queryOptions: Partial<QueryOptions>;
}

/**
 * A query builder for GraphQL queries. This is used to incrementally create,
 * save, and modify GraphQL queries without lots of string processing.
 *
 * All query modifications return a new Query object, so previous versions can
 * be saved and reused.
 */
export default class Query<
  M extends typeof ApplicationModel | ApplicationModelConstructor<any>,
  I extends InstanceType<M>
> {
  [immerable] = true;

  model: M | undefined;
  /**
   * This is used to track the graphql types for filters. Subqueries will set
   * it to the field args from the model
   */
  paramTypes: Record<string, string | undefined>;
  attrs: Set<string>;
  arguments: Record<string, any>;
  filters: Record<string, any>;
  sort: Record<string, SortOrder>;
  subqueries: Record<string, Query<any, any>>;
  _stats: Set<string>;
  single: boolean;
  unions: Record<string, Query<any, any>>;

  /**
   * Creates a new Query for `model`
   *
   * @param model The model used as the result of the query
   */
  constructor(model?: M) {
    this.model = model;
    this.paramTypes = { filters: this.model?.filterType };

    this.attrs = new Set();
    this.arguments = {};
    this.filters = {};
    this.sort = {};
    this.subqueries = {};
    this._stats = new Set();
    this.single = false;
    this.unions = null;
  }

  /**
   * Adds `attrs` to the attributes selected by the query.
   *
   * Example: query.select(['id', 'name']);
   *
   * @returns A Query object selecting `attrs`
   */
  select(attrs: Selections): this;
  select(...attrs: Selection[]): this;
  select(attrs: any) {
    if (!attrs.forEach) {
      attrs = Array.from(arguments);
    }

    return produce(this, draft => {
      attrs.forEach(attr => {
        draft.attrs.add(attr);
      });
    });
  }

  /**
   * Adds all non-relationship attributes to the query selection
   *
   * Example: query.selectAll().find(1)
   */
  selectAll() {
    const attrs = Object.entries(this.model.fields)
      .filter(([_, config]) => config.type === 'attr' && config.readable)
      .map(([name, _]) => name);
    return this.select(attrs);
  }

  /**
   * Unions this query with another query, using GraphQL union
   * types. Should only be used in subqueries.
   *
   * Example: new Query(Epic).select(['id', 'name'])
   *            .union(new Query(Feature).select(['id', 'name']));
   *
   * @param query The other query to union with this one
   */
  union(query?: Query<any, any>) {
    return produce(this, draft => {
      if (!draft.unions) {
        draft.unions = {};
        draft.unions[draft.model.typename] = produce(draft, subQueryDraft => {
          subQueryDraft.unions = {};
        });
      }

      if (query) {
        // @ts-ignore
        draft.unions[query.model.typename] = query;
      }
    });
  }

  /**
   * When paginating, adds `stats` to the page attributes selected by
   * the query.
   *
   * Example: query.stats(['isLastPage']);
   *
   * @param attrs The attributes to select
   * @returns A Query object selecting `attrs` on the page level
   */
  stats(attrs: string[]) {
    return produce(this, draft => {
      attrs.forEach(attr => draft._stats.add(attr));
    });
  }

  /**
   * When paginating, selects page `pageNumber`
   *
   * Example: query.page(2)
   *
   * @param pageNumber The page number to select
   * @returns A Query object selecting that page
   */
  page(pageNumber: number) {
    return this.argument({ page: pageNumber });
  }

  /**
   * When paginating, sets the number of results per page
   *
   * Example: query.per(50)
   *
   * @param perPage The number of results per page
   * @returns An updated Query object
   */
  per(perPage: number) {
    return this.argument({ per: perPage });
  }

  /**
   * Adds bare, top-level arguments to the query
   *
   * Example: query.argument({timeout: 1000})
   *
   * @param args The top-level arguments to add
   * @returns An updated Query object
   */
  argument(args: typeof this.arguments) {
    return produce(this, draft => {
      draft.arguments = { ...draft.arguments, ...args };
    });
  }

  /**
   * Merges arguments into the query
   *
   * Example: query.argument({projectId: 1}).argumentMerge({projectId: 2})
   *
   * The above example will generate arguments of projectId: [1,2]
   *
   * @param object args The args to add to the query
   * @returns An updated Query object with those args added
   */
  argumentMerge(args: typeof this.arguments) {
    if (!args) return this;

    return this.argument(mergeArguments(this.arguments, args));
  }

  /**
   * Adds filters to the query
   *
   * Example: query.where({projectId: 15})
   *
   * @param filters The filters to add to the query
   * @returns An updated Query object with those filters added
   */
  where(filters: typeof this.filters) {
    return this.rewhere({ ...this.filters, ...filters });
  }

  /**
   * Merges filters into the existing filters
   *
   * Example: query.whereMerge({projectId: 1}).whereMerge({projectId: 2})
   *
   * The above example would be the same as query.where({projectId: [1,2]})
   *
   * @param filters The filters to add to the query
   * @returns An updated Query object with those filters added
   */
  whereMerge(filters: typeof this.filters) {
    if (!filters) return this;

    return this.rewhere(mergeArguments(this.filters, filters));
  }

  /**
   * Resets the filters on the query
   *
   * Example: query.rewhere({projectId: 15})
   *
   * @param filters The filters to set on the query
   * @returns An updated Query object with those filters added and all others removed
   */
  rewhere(filters: typeof this.filters) {
    return produce(this, draft => {
      draft.filters = filters;
    });
  }

  /**
   * Adds a criteria for ordering. Will be added to the end of the order list.
   *
   * Example: query.order({createdAt: 'ASC'});
   *
   * @param criteria A one-key object where the key is an order attribute name and the value is a direction
   * @returns An updated Query object with that order criteria added to it
   */
  order(criteria: typeof this.sort) {
    return this.reorder({ ...this.sort, ...criteria });
  }

  /**
   * Resets all criteria for ordering.
   *
   * Example: query.reorder({createdAt: 'ASC'});
   *
   * @param criteria A one-key object where the key is an order attribute name and the value is a direction
   * @returns An updated Query object with that order criteria set to it
   */
  reorder(criteria: typeof this.sort) {
    return produce(this, draft => {
      draft.sort = criteria ?? {};
    });
  }

  /**
   * When called, the query will select a single object instead of multiple.
   *
   * Example: query.first();
   *
   * @returns An updated Query object selecting a single record
   */
  first() {
    return produce(this, draft => {
      draft.single = true;
    });
  }

  /**
   * When called the query will select multiple objects instead of single.
   */
  multiple() {
    return produce(this, draft => {
      draft.single = false;
    });
  }

  /**
   * Adds a subquery to select deeply nested records.
   *
   * Example: Workflow.select(['id']).merge({
   *   workflowStatuses: WorkflowStatus.select([
   *     'id',
   *     'name',
   *     'color',
   *     'position',
   *   ]),
   * });
   *
   * @param subqueries The subqueries to merge into this query, where the key is an attribute name and the value is a Query object.
   * @returns An updated Query object including the nested subqueries
   */
  merge(subqueries: Subqueries) {
    let newQueries: Record<string, Query<any, any>> = {};

    Object.entries(subqueries).forEach(([name, value]) => {
      newQueries = produce(newQueries, draft => {
        // Convert bare lists of columns into generic subqueries that
        // cannot run on their own.
        //
        // TODO: at the moment subqueries cannot be paginated. Only the top
        // level lists can be paginated and return nodes. This can be addressed
        // by adding metadata to the hasMany(). Right now just setting single=true
        if (Array.isArray(value)) {
          // @ts-ignore
          draft[name] = new Query().select(value).first();
        } else if (value instanceof Query) {
          // @ts-ignore
          draft[name] = produce(value, draft => {
            const field = this.model.fields[name];

            if (field && 'args' in field) {
              draft.paramTypes = field.args;
            }

            if (!canPaginate(value.arguments)) {
              draft.single = true;
            }
          });
        }
      });
    });

    return produce(this, draft => {
      // @ts-ignore
      draft.subqueries = { ...draft.subqueries, ...newQueries };
    });
  }

  /**
   * Deeply merges a collection of subqueries into a query. Rather than replacing an existing
   * subquery, it appends/merges the query into the existing one.
   *
   * @param subqueries The subqueries to merge into the query.
   * @returns An updated Query including the nested subqueries.
   */
  deepMerge(subqueries: Record<string, Subquery>) {
    let scope = this;

    Object.keys(subqueries).forEach(name => {
      const subquery = subqueries[name];
      if (name in scope.subqueries) {
        if (Array.isArray(subquery)) {
          scope = scope.merge({
            [name]: scope.subqueries[name].select(subquery),
          });
        } else if (subquery instanceof Query) {
          scope = scope.merge({
            [name]: scope.subqueries[name]
              .select(subquery.attrs)
              .deepMerge(subquery.subqueries)
              .argumentMerge(subquery.arguments)
              .whereMerge(subquery.filters),
          });
        } else {
          scope = scope.merge({
            [name]: scope.subqueries[name].deepMerge(subquery),
          });
        }
      } else {
        scope = scope.merge({ [name]: subquery });
      }
    });

    return scope;
  }

  /**
   * Executes this query, finding a single record by ID.
   *
   * @param id The object id to request
   * @returns The requested model, or undefined if it couldn't be found.
   */
  async find(
    id: string,
    options: FindOptions = { queryOptions: {} }
  ): Promise<I> {
    const query = this.argument({ id }).first();

    try {
      const data = await ApplicationModel.client.query(query.query, {
        variables: query.queryVariables,
        ...options.queryOptions,
      });

      if (data) {
        return modelObject<I>(data, { query })[query.queryName];
      } else {
        throw new NotFoundError(`Could not find ${query.queryName} ${id}`);
      }
    } catch (error) {
      if (error instanceof NotFoundError) {
        // Insert more info into the not found error
        throw new NotFoundError(
          `Could not find ${query.queryName} ${id}: ${error.message}`
        );
      } else {
        throw error;
      }
    }
  }

  /**
   * Executes this query, finding a single record by the specified filter.
   *
   * @param {object} filters The filters to add to the query
   * @returns {Promise<I|undefined>} The requested model, or undefined if it couldn't be found.
   */
  async findBy(filters: Record<string, any>): Promise<I | undefined> {
    const data = await this.where(filters).per(1).all();

    if (data) {
      return data[0];
    }
  }

  /**
   * Executes this query.
   *
   * @returns The requested models, or undefined if it couldn't be found.
   */
  async all(): Promise<ModelCollection<I>> {
    const data = await ApplicationModel.client.query(this.queryString, {
      variables: this.queryVariables,
    });

    if (data) {
      return modelObject(data, { query: this })[this.queryName];
    }
  }

  /**
   * Fetch *all* records from multiple pages. `pageCallback` will be
   * called for each page as it arrives. If no callback is specified,
   * will return all of the records found. If a callback is specified,
   * it is assumed that the caller will handle the records, and the
   * return value will be empty.
   *
   * @param pageCallback a callback called with a list of records for every page as it arrives
   * @param options
   * @returns An array of the requested records, if no callback is given
   */
  async findInBatches(pageCallback?: FindInBatchesCallback<I>): Promise<I[]>;
  async findInBatches(options?: FindInBatchesOptions): Promise<I[]>;
  async findInBatches(
    pageCallback: FindInBatchesCallback<I>,
    options: FindInBatchesOptions
  ): Promise<I[]>;
  async findInBatches(
    pageCallback?: FindInBatchesCallback<I> | FindInBatchesOptions,
    options: FindInBatchesOptions = {}
  ): Promise<I[]> {
    if (typeof pageCallback === 'object' && !isNil(pageCallback)) {
      options = pageCallback;
      pageCallback = null;
    }

    // Place to store the found records
    const totalRecords: I[] = [];

    // Create a callback if one wasn't given
    let realPageCallback: FindInBatchesCallback<I> =
      typeof pageCallback === 'function'
        ? pageCallback
        : page => {
            totalRecords.push(...page.materializeRecords());
          };

    // Fetch the first page and use that to determine the number to pull
    const firstPage = await this.page(1)
      .stats(['currentPage', 'isLastPage', 'totalPages'])
      .all();

    await realPageCallback(firstPage);

    // Create a queue, given it the required concurrency and let that fetch the
    // next pages
    const queue = new Queue();
    const pageResults = [];

    queue.concurrency = options.concurrency || 1;

    for (let page = 2; page <= firstPage.totalPages; page++) {
      let thisPage = page;

      queue.push(cb => {
        this.page(page)
          .stats(['currentPage', 'isLastPage'])
          .all()
          .then(r => {
            pageResults[thisPage - 2] = r;
            cb(null);
          });
      });
    }

    return new Promise((resolve, reject) => {
      queue.on('end', async () => {
        await Promise.all(pageResults.map(result => realPageCallback(result)));
        resolve(totalRecords);
      });

      queue.start(err => {
        if (err) reject(err);
      });
    });
  }

  /**
   * Returns the name for the query based on the model used to construct it.
   *
   * @returns The query name
   */
  get queryName() {
    let queryName = camelize(this.model.typename, false);
    if (!this.single) {
      queryName = pluralize(queryName);
    }
    return queryName;
  }

  /**
   * Returns the name of the constructed graphql query
   */
  get queryFunctionName() {
    return classify(this.queryName);
  }

  /**
   * Returns the variables to send with the graphql query
   */
  get queryVariables() {
    return this.queryFragment.toVariables();
  }

  /**
   * Returns the parameter list to add to the graphql query based on the set
   * filters
   */
  get queryParametersString() {
    const params = this.queryFragment.toParameters();

    if (params.length === 0) return '';

    return (
      '(' + params.map(([name, type]) => `$${name}: ${type}`).join(', ') + ')'
    );
  }

  /**
   * Returns the DocumentNode version of the GraphQL query
   */
  get query() {
    if (!this.model.typename) {
      throw new Error(
        `Model ${this.model} has no typename and thus cannot be used in query builder. Did you forget to add an annotation like \`static typename = 'releases';\` to the model class?`
      );
    }

    const fragment = this.queryFragment;
    return fragment.toDocument(this.queryFunctionName);
  }

  /**
   * Returns the plain string version of the GraphQL query corresponding to this object.
   *
   * @returns {string} A GraphQL query
   */
  get queryString() {
    return print(this.query);
  }

  /**
   * Returns if this query is a subset of another query, i.e. if you merged the
   * fragment of this query into the other query then other query's fragment
   * would be the same.
   */
  isSubsetOf(other: Query<any, any>) {
    return this.queryFragment.isSubsetOf(other.queryFragment);
  }

  /**
   * @returns {string?} the key that will likely be used to cache the result of
   * this query, or undefined if it cannot be determined from the information
   * in this query.
   */
  get cacheId() {
    if (this.single && this.arguments['id'] && this.attrs.has('id')) {
      return ApplicationModel.client.cacheId({
        __typename: this.model.typename,
        ...this.filters,
        ...this.arguments,
      });
    }
  }

  /**
   * @hidden
   */
  buildFragmentArguments(fragment: Fragment) {
    // Add the filter parameter
    if (this.filters && Object.keys(this.filters).length > 0) {
      fragment = fragment.argument(
        'filters',
        this.filters,
        this.paramTypes.filters
      );
    }

    // Add the sort parameter
    if (Object.keys(this.sort).length > 0) {
      fragment = fragment.argument(
        'order',
        Object.entries(this.sort).reduce(
          (acc, [name, direction]) => [...acc, { name, direction }],
          []
        ),
        `[${this.model.internalTypename.replace(/(.*::)?/, '')}OrderClause!]`
      );
    }

    // Add any additionally defined parameters (id, page, etc)
    fragment = Object.entries(this.arguments || {}).reduce(
      (fragment, [name, value]) =>
        fragment.argument(name, value, this.paramTypes[name]),
      fragment
    );

    return fragment;
  }

  /**
   * Either add the union fragments (if this is a union query) or the subqueries
   * and attributes, to a fragment
   *
   * @hidden
   */
  buildFragmentSubqueries(fragment: Fragment): Fragment {
    // Get the fragments from the union queries. If there are any then they'll
    // take the place of this fragment
    const unionFragments = Object.values(this.unions || {}).map(query =>
      query.buildQueryFragment(null, true)
    );

    if (unionFragments.length > 0) {
      return fragment.fragment(...unionFragments);
    } else {
      // Get all the subquery fragments and set their names to the subquery
      // attr
      const subqueryFragments = Object.entries(this.subqueries).map(
        ([name, subquery]) => subquery.buildQueryFragment(name)
      );

      const attrFragments = Array.from(this.attrs)
        .filter(attr => typeof attr !== 'string')
        .flatMap((attr: SelectionObject) =>
          Object.entries(attr).map(([name, args]) =>
            Object.entries(args).reduce(
              (acc, [arg, value]) => acc.argument(arg, value),
              new Fragment(name)
            )
          )
        );

      return fragment
        .fragment(...subqueryFragments)
        .fragment(...attrFragments)
        .attr(
          ...Array.from(this.attrs).filter(attr => typeof attr === 'string')
        );
    }
  }

  /**
   * Build the graphql fragment tree for this query
   *
   * @hidden
   * @param name
   * @param union Indicates this query should be built as a union
   */
  buildQueryFragment(name: string = this.queryName, union: boolean = false) {
    let fragment = new Fragment(name, this.model?.typename);

    if (union) {
      fragment = fragment.union();
    } else {
      fragment = this.buildFragmentArguments(fragment);
    }

    if (!union && !this.single) {
      // For list queries with paginated output we create a completely new
      // fragment that becomes the top level fragments only child, set the
      // query to 'nodes' and add all the attrs and subqueries to that
      return fragment
        .fragment(this.buildFragmentSubqueries(new Fragment('nodes')))
        .attr(...Array.from(this._stats));
    }

    return this.buildFragmentSubqueries(fragment);
  }

  /**
   * Get the resolved immutable graphql fragment tree for this query
   */
  get queryFragment() {
    return memoize(this, 'queryFragment', () =>
      this.buildQueryFragment().resolve()
    );
  }
}
